"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get CalculatorReviewQuestion () {
        return CalculatorReviewQuestion;
    },
    get DateReviewQuestion () {
        return DateReviewQuestion;
    },
    get GoalCreationReviewQuestion () {
        return GoalCreationReviewQuestion;
    },
    get GoalDiscussionReviewQuestion () {
        return GoalDiscussionReviewQuestion;
    },
    get InstructionsReviewQuestion () {
        return InstructionsReviewQuestion;
    },
    get MultilineReviewQuestion () {
        return MultilineReviewQuestion;
    },
    get MultipleChoiceReviewQuestion () {
        return MultipleChoiceReviewQuestion;
    },
    get MultipleSelectReviewQuestion () {
        return MultipleSelectReviewQuestion;
    },
    get NumericReviewQuestion () {
        return NumericReviewQuestion;
    },
    get RatingScaleOption () {
        return RatingScaleOption;
    },
    get RatingScaleReviewQuestion () {
        return RatingScaleReviewQuestion;
    },
    get ReviewQuestion () {
        return ReviewQuestion;
    },
    get ReviewQuestionWithDefault () {
        return ReviewQuestionWithDefault;
    },
    get StarsReviewQuestion () {
        return StarsReviewQuestion;
    },
    get SubQuestionReference () {
        return SubQuestionReference;
    },
    get TextReviewQuestion () {
        return TextReviewQuestion;
    }
});
const _zod = require("zod");
const _models = require("@performyard/models");
const _utilsisomorphic = require("@performyard/utils-isomorphic");
const _helper = require("./helper");
const RatingScaleOption = _zod.z.object({
    value: _zod.z.number().optional(),
    name: _models.RatingScaleOptionModel.shape.name,
    description: _models.RatingScaleOptionModel.shape.description
});
const SubQuestionReference = _zod.z.object({
    id: _utilsisomorphic.transformObjectId,
    weight: _zod.z.number().gt(0)
});
/**
 * Base properties for all question types
 */ const BaseReviewQuestion = _zod.z.object({
    _id: _utilsisomorphic.transformObjectId.optional(),
    question: _zod.z.string().max(3000).optional(),
    subtext: _zod.z.string().max(12000).optional(),
    isRequired: _zod.z.boolean().optional()
});
/**
 * Question-specific shapes (without base fields)
 * These are merged with Base schemas to create Response and Request versions
 * This eliminates duplication - validations defined once
 */ const textQuestionShape = _zod.z.object({
    type: _zod.z.literal("text")
});
const multilineQuestionShape = _zod.z.object({
    type: _zod.z.literal("multiline")
});
const dateQuestionShape = _zod.z.object({
    type: _zod.z.literal("date")
});
const multipleSelectQuestionBase = _zod.z.object({
    type: _zod.z.literal("multiple-select"),
    choices: _zod.z.array(_zod.z.string()).min(1).max(20),
    multiSelectMin: _zod.z.number().gt(0).optional(),
    multiSelectMax: _zod.z.number().gt(0).optional()
});
const applyMultipleSelectRefinements = (schema)=>{
    return schema.refine((data)=>{
        // multiSelectMax cannot exceed number of choices
        if (data.multiSelectMax !== undefined && data.multiSelectMax > data.choices.length) {
            return false;
        }
        return true;
    }, {
        message: "multiSelectMax cannot exceed number of choices",
        path: [
            "multiSelectMax"
        ]
    }).refine((data)=>{
        // multiSelectMin must be <= multiSelectMax
        if (data.multiSelectMin !== undefined && data.multiSelectMax !== undefined && data.multiSelectMin > data.multiSelectMax) {
            return false;
        }
        return true;
    }, {
        message: "multiSelectMin must be less than or equal to multiSelectMax",
        path: [
            "multiSelectMin"
        ]
    });
};
const multipleChoiceQuestionShape = _zod.z.object({
    type: _zod.z.literal("multiple-choice"),
    choices: _zod.z.array(_zod.z.string()).min(1).max(20)
});
const ratingScaleQuestionShape = _zod.z.object({
    type: _zod.z.literal("rating-scale"),
    choices: _zod.z.array(RatingScaleOption).min(1)
});
const numericQuestionBase = _zod.z.object({
    type: _zod.z.literal("numeric"),
    decimals: _zod.z.boolean().optional(),
    min: _zod.z.number().optional(),
    max: _zod.z.number().optional()
});
const applyNumericRefinements = (schema)=>{
    return schema.refine((data)=>{
        // min must be less than max if both are provided
        if (data.min !== undefined && data.max !== undefined && data.min >= data.max) {
            return false;
        }
        return true;
    }, {
        message: "min must be less than max",
        path: [
            "min"
        ]
    });
};
const goalCreationQuestionShape = _zod.z.object({
    type: _zod.z.literal("goal-creation")
});
const goalDiscussionQuestionBase = _zod.z.object({
    type: _zod.z.literal("goal-discussion"),
    addScoring: _zod.z.boolean().optional(),
    scoringMin: _zod.z.number().optional(),
    scoringMax: _zod.z.number().optional(),
    addScaling: _zod.z.array(RatingScaleOption).optional(),
    scoreWeights: _zod.z.boolean().optional()
});
const applyGoalDiscussionRefinements = (schema)=>{
    return schema.refine((data)=>{
        // Cannot have both addScoring and addScaling
        if (data.addScoring && data.addScaling && data.addScaling.length > 0) {
            return false;
        }
        return true;
    }, {
        message: "Cannot have both addScoring and addScaling",
        path: [
            "addScoring"
        ]
    }).refine((data)=>{
        // If addScoring is true, scoringMin and scoringMax are required
        if (data.addScoring && (data.scoringMin === undefined || data.scoringMax === undefined)) {
            return false;
        }
        return true;
    }, {
        message: "scoringMin and scoringMax are required when addScoring is true",
        path: [
            "scoringMin"
        ]
    }).refine((data)=>{
        // If addScaling is provided (not undefined), it must have at least one rating
        if (data.addScaling !== undefined && data.addScaling.length === 0) {
            return false;
        }
        return true;
    }, {
        message: "addScaling must have at least one rating when provided",
        path: [
            "addScaling"
        ]
    }).refine((data)=>{
        // scoringMin must be less than scoringMax if both are provided
        if (data.addScoring && data.scoringMin !== undefined && data.scoringMax !== undefined && data.scoringMin >= data.scoringMax) {
            return false;
        }
        return true;
    }, {
        message: "scoringMin must be less than scoringMax",
        path: [
            "scoringMin"
        ]
    });
};
const calculatorQuestionShape = _zod.z.object({
    type: _zod.z.literal("calculator"),
    aggregation: _zod.z.enum([
        "sum",
        "average"
    ]),
    questions: _zod.z.array(SubQuestionReference).min(1),
    isHidden: _zod.z.boolean().optional()
}).strict();
const instructionsQuestionShape = _zod.z.object({
    type: _zod.z.literal("instructions")
}).strict();
const starsQuestionShape = _zod.z.object({
    type: _zod.z.literal("stars")
});
const TextReviewQuestion = BaseReviewQuestion.merge(textQuestionShape);
const MultilineReviewQuestion = BaseReviewQuestion.merge(multilineQuestionShape);
const DateReviewQuestion = BaseReviewQuestion.merge(dateQuestionShape);
const MultipleSelectReviewQuestion = applyMultipleSelectRefinements(BaseReviewQuestion.merge(multipleSelectQuestionBase));
const MultipleChoiceReviewQuestion = BaseReviewQuestion.merge(multipleChoiceQuestionShape);
const RatingScaleReviewQuestion = BaseReviewQuestion.merge(ratingScaleQuestionShape);
const NumericReviewQuestion = applyNumericRefinements(BaseReviewQuestion.merge(numericQuestionBase));
const GoalCreationReviewQuestion = BaseReviewQuestion.merge(goalCreationQuestionShape);
const GoalDiscussionReviewQuestion = applyGoalDiscussionRefinements(BaseReviewQuestion.merge(goalDiscussionQuestionBase));
const CalculatorReviewQuestion = BaseReviewQuestion.omit({
    isRequired: true
}).merge(calculatorQuestionShape);
const InstructionsReviewQuestion = BaseReviewQuestion.omit({
    isRequired: true
}).merge(instructionsQuestionShape);
const StarsReviewQuestion = BaseReviewQuestion.merge(starsQuestionShape);
const ReviewQuestion = _zod.z.discriminatedUnion("type", [
    TextReviewQuestion,
    MultilineReviewQuestion,
    DateReviewQuestion,
    MultipleSelectReviewQuestion,
    MultipleChoiceReviewQuestion,
    RatingScaleReviewQuestion,
    NumericReviewQuestion,
    GoalCreationReviewQuestion,
    GoalDiscussionReviewQuestion,
    CalculatorReviewQuestion,
    InstructionsReviewQuestion,
    StarsReviewQuestion
]);
const ReviewQuestionWithDefault = _zod.z.preprocess(_helper.convertQuestionModelToServiceContract, ReviewQuestion);
